import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    CircularProgress,
} from "@mui/material";

// =========================
// Utils
// =========================

const fmtDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

const fmtDateTime = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const date = fmtDate(val);
    const time = d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });
    return `${date} ${time}`;
};

const firstNonEmpty = (...vals) => {
    for (const v of vals) {
        if (v === 0) return 0;
        if (v !== null && v !== undefined) {
            const s = String(v);
            if (s.trim() !== "") return v;
        }
    }
    return "";
};

// =========================
// Table helpers
// =========================

const Row = ({ label, value }) => (
    <tr>
        <td
            style={{
                width: "35%",
                padding: "8px",
                border: "1px solid #ddd",
                fontWeight: 600,
                verticalAlign: "top",
                backgroundColor: "#f9f9f9",
            }}
        >
            {label}
        </td>
        <td
            style={{
                width: "65%",
                padding: "8px",
                border: "1px solid #ddd",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                verticalAlign: "top",
            }}
        >
            {value ?? "—"}
        </td>
    </tr>
);

const Section = ({ title }) => (
    <tr>
        <td
            colSpan={2}
            style={{
                padding: "12px",
                border: "1px solid #ddd",
                fontWeight: "bold",
                color: "#fff",
                backgroundColor: "#356F60",
                fontSize: "14px",
            }}
        >
            {title}
        </td>
    </tr>
);

// =========================
// MAIN PREVIEW COMPONENT
// =========================

const MHISClientFormPreview = ({ data }) => {
    const [form, setForm] = useState({});
    const [mhis, setMhis] = useState({});
    const [mf, setMf] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { patientFormId, formTitle } = data || {};

    useEffect(() => {
        if (!patientFormId) {
            setLoading(false);
            setError("No Patient Form ID provided");
            return;
        }

        let active = true;

        const fetchData = async () => {
            setLoading(true);
            setError("");

            try {
                const token = localStorage.getItem(
                    "neuro_patientportal_auth_token"
                );
                if (!token) {
                    throw new Error("Authentication token not found. Please log in.");
                }

                const headers = { Authorization: `Bearer ${token}` };
                const baseUrl = process.env.REACT_APP_API_URL;

                const [formRes, mhisRes] = await Promise.all([
                    fetch(
                        `${baseUrl}/Forms/GetPatientFormsbyId?Id=${patientFormId}`,
                        { headers }
                    ),
                    fetch(
                        `${baseUrl}/MHIS/GetMHISFormbyFormId/?Id=${patientFormId}`,
                        { headers }
                    ),
                ]);

                if (!active) return;

                if (!formRes.ok) {
                    throw new Error(`Form load failed (${formRes.status})`);
                }

                const formRaw = await formRes.json();
                const formInfo = Array.isArray(formRaw)
                    ? formRaw[0] || {}
                    : formRaw || {};

                let mhisData = {};
                let mfData = {};
                if (mhisRes.ok) {
                    const mhisJson = await mhisRes.json();
                    mhisData = mhisJson?.mhis || {};
                    mfData = mhisData.mf || {};
                }

                if (active) {
                    setForm(formInfo);
                    setMhis(mhisData);
                    setMf(mfData);
                }
            } catch (err) {
                if (active) {
                    console.error("Error fetching MHIS data:", err);
                    setError(err.message || "Failed to load MHIS Client form.");
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchData();
        return () => {
            active = false;
        };
    }, [patientFormId]);

    // =========================
    // LOADING / ERROR
    // =========================

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    height: "60vh",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" align="center" sx={{ p: 3 }}>
                {error}
            </Typography>
        );
    }

    // =========================
    // BASIC DATA
    // =========================

    const headerTitle =
        firstNonEmpty(formTitle, form.customFormTitle) ||
        "MHIS Client Level Data Form";

    const dosVal = fmtDate(form.assessmentDate);
    const fileNo = firstNonEmpty(form.accountNo, form.fileno) || "—";
    const clientName =
        firstNonEmpty(form.patientName, mhis.clientName) || "—";
    const dobVal = fmtDate(firstNonEmpty(form.dob, form.dateOfBirth, mf.dob));

    const rehabWorkerName = form.messagerehabWorker || "";
    const rehabWorkerSignedOn = fmtDateTime(form.rehabWorkerSignatureDATE);
    const practitionerName = form.messagepractitioner || "";
    const practitionerSignedOn = fmtDateTime(
        form.healthPractitionerSignatureDATE
    );
    const staffName = form.messageinternSign || "";
    const staffSignedOn = fmtDateTime(form.staffsignatureDATE);
    const supervisorName = form.messageSupervisorSign || "";
    const supervisorSignedOn = fmtDateTime(form.supervisorSignatureDATE);
    const directorName = form.messagetreatmentdirector || "";
    const directorSignedOn = fmtDateTime(form.directorDate);

    const dateField = (val) => (val ? fmtDate(val) : "—");

    // =========================
    // RENDER
    // =========================

    return (
        <Paper
            sx={{
                p: 0,
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
            }}
        >
            {/* Sticky Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: "-16px",
                    zIndex: (t) => t.zIndex.appBar,
                    bgcolor: "#fff",
                    p: 4,
                    pb: 0,
                    mb: 3,
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ position: "absolute", top: 16, left: 16 }}
                >
                    <strong>DOS:</strong> {dosVal}
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        position: "absolute",
                        top: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontWeight: "bold",
                        color: "#356F60",
                    }}
                >
                    {headerTitle}
                </Typography>

                <Box sx={{ height: 28 }} />
                <Divider />
            </Box>

            {/* Patient strip */}
            <Box sx={{ px: 4 }}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Grid item xs={4}>
                        <Typography variant="body2">
                            <strong>File #:</strong> {fileNo}
                        </Typography>
                    </Grid>
                    <Grid item xs={4} textAlign="center">
                        <Typography variant="body2">
                            <strong>Client Name:</strong> {clientName}
                        </Typography>
                    </Grid>
                    <Grid item xs={4} textAlign="right">
                        <Typography variant="body2">
                            <strong>DOB:</strong> {dobVal}
                        </Typography>
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 3 }} />
            </Box>

            {/* Main content */}
            <Box sx={{ px: 4, pb: 4, maxWidth: "100%", overflowX: "hidden" }}>
                <table
                    style={{
                        width: "100%",
                        tableLayout: "fixed",
                        borderCollapse: "collapse",
                        fontSize: "12pt",
                        background: "white",
                        wordWrap: "break-word",
                    }}
                >
                    <tbody>
                        {/* HEADER / STATUS */}
                        <Section title="Header / Reporting Information" />
                        <Row
                            label="Reporting Year"
                            value={mf.statusReportingYear || "—"}
                        />
                        <Row
                            label="Reporting Period (code)"
                            value={mf.statusReportingPeriod || "—"}
                        />
                        <Row
                            label="Client Start Date"
                            value={dateField(mf.clientStartDate || mhis.illnessDate)}
                        />
                        <Row
                            label="Client End Date"
                            value={dateField(mf.clientEndDate)}
                        />
                        <Row
                            label="Status Update Date"
                            value={dateField(mf.statusUpdateDate)}
                        />
                        <Row
                            label="Current Client Status (code)"
                            value={mf.currentClientStatus || "—"}
                        />
                        <Row
                            label="Discontinuance Code (code)"
                            value={mf.discontinuanceCodes || "—"}
                        />

                        {/* PAYMENT & PROGRAMS */}
                        <Section title="Payment and Programs" />
                        <Row
                            label="Payment Source (code)"
                            value={mf.paymentSource || "—"}
                        />
                        <Row
                            label="Reason Grant Funded (code)"
                            value={mf.reasonGrandFunded || "—"}
                        />
                        <Row label="Grant Type (codes)" value={mf.grantType || "—"} />
                        <Row
                            label="Current Mental Health Program(s) (codes)"
                            value={mf.currentMentalHealthProgram || "—"}
                        />
                        <Row
                            label="Legal Status (code)"
                            value={mf.legalStatus || "—"}
                        />

                        {/* DEMOGRAPHICS */}
                        <Section title="Demographic and Residence Information" />
                        <Row
                            label="Gender"
                            value={mhis.gender || mf.gender || "—"}
                        />
                        <Row label="Race" value={mhis.race || mf.race || "—"} />
                        <Row
                            label="Ethnicity"
                            value={mhis.ethnicity || mf.ethnicity || "—"}
                        />
                        <Row
                            label="County of Residence (code)"
                            value={mf.countyOfResidence || "—"}
                        />
                        <Row
                            label="Reside on Reservation (code)"
                            value={mf.resideonReservation || "—"}
                        />
                        <Row
                            label="Tribal Enrollment (code)"
                            value={mf.tribalEnrollment || "—"}
                        />
                        <Row
                            label="Residential Status (code)"
                            value={mf.residentialStatus || "—"}
                        />
                        <Row
                            label="Housing Status (code)"
                            value={mf.housingStatus || "—"}
                        />
                        <Row
                            label="Housing Informed Choice (code)"
                            value={mf.housingInformedChoice || "—"}
                        />
                        <Row
                            label="Barriers to Moving (codes, comma-separated)"
                            value={mf.barriersToMoving || "—"}
                        />
                        <Row
                            label="Housing Preferences / Needs (codes)"
                            value={mf.housingPreferences || "—"}
                        />

                        {/* DIAGNOSIS / WHODAS / LOCUS */}
                        <Section title="Diagnosis and Assessments" />
                        <Row
                            label="Primary Diagnosis Code"
                            value={mf.primaryDiagnosis || "—"}
                        />
                        <Row
                            label="Secondary Diagnosis Code"
                            value={mf.secondaryDiagnosis || "—"}
                        />
                        <Row
                            label="Tertiary Diagnosis Code"
                            value={mf.tertiaryDiagnosis || "—"}
                        />
                        <Row
                            label="Diagnostic Assessment Date"
                            value={dateField(mf.diagnosticAssessmentDate)}
                        />
                        <Row
                            label="WHODAS Score"
                            value={
                                mf.whodasItemVersion1 !== null &&
                                    mf.whodasItemVersion1 !== undefined
                                    ? mf.whodasItemVersion1
                                    : "—"
                            }
                        />
                        <Row
                            label="Substance Abuse Screening (code)"
                            value={mf.substanceAbuseScreening || "—"}
                        />
                        <Row
                            label="LOCUS Composite Score"
                            value={
                                mf.locusCompositeScoreRange !== null &&
                                    mf.locusCompositeScoreRange !== undefined
                                    ? mf.locusCompositeScoreRange
                                    : "—"
                            }
                        />
                        <Row
                            label="LOCUS Assessment Date"
                            value={dateField(mf.dateLOCUSAssessmentCompleted)}
                        />
                        <Row
                            label="LOCUS Level of Care (code)"
                            value={mf.doesLevelCareORServiceLOCUS || "—"}
                        />

                        {/* HEALTH (SHORT VERSION) */}
                        <Section title="Basic Health Indicators" />
                        <Row
                            label="Height (inches)"
                            value={
                                mf.heightInInches !== null &&
                                    mf.heightInInches !== undefined
                                    ? mf.heightInInches
                                    : "—"
                            }
                        />
                        <Row
                            label="Weight (lbs)"
                            value={
                                mf.weight !== null && mf.weight !== undefined
                                    ? mf.weight
                                    : "—"
                            }
                        />
                        <Row
                            label="Systolic Blood Pressure"
                            value={
                                mf.systolicBloodPressure !== null &&
                                    mf.systolicBloodPressure !== undefined
                                    ? mf.systolicBloodPressure
                                    : "—"
                            }
                        />
                        <Row
                            label="Diastolic Blood Pressure"
                            value={
                                mf.diastolicBloodPressure !== null &&
                                    mf.diastolicBloodPressure !== undefined
                                    ? mf.diastolicBloodPressure
                                    : "—"
                            }
                        />
                        <Row
                            label="LDL"
                            value={
                                mf.ldl !== null && mf.ldl !== undefined ? mf.ldl : "—"
                            }
                        />
                        <Row
                            label="Blood Sugar Level"
                            value={
                                mf.bloodSugarLevel !== null &&
                                    mf.bloodSugarLevel !== undefined
                                    ? mf.bloodSugarLevel
                                    : "—"
                            }
                        />

                        {/* COMMUNITY SUPPORT PROGRAM (SHORT) */}
                        <Section title="Community Support Program (codes)" />
                        <Row
                            label="Direct Services Used"
                            value={mf.specifyDirectServicesUsed || "—"}
                        />
                        <Row
                            label="Direct Subsidies Used"
                            value={mf.specifyDirectSubsidiesUsed || "—"}
                        />
                        <Row
                            label="Outreach Services Used"
                            value={mf.specifyOutreachServicesUsed || "—"}
                        />

                        {/* SIGNATURES */}
                        <Section title="Signatures" />
                        <Row
                            label="Mental Health Rehab Worker Signature"
                            value={
                                rehabWorkerName
                                    ? `${rehabWorkerName}${rehabWorkerSignedOn && rehabWorkerSignedOn !== "—"
                                        ? ` (signed on ${rehabWorkerSignedOn})`
                                        : ""
                                    }`
                                    : "—"
                            }
                        />
                        <Row
                            label="Mental Health Practitioner Signature"
                            value={
                                practitionerName
                                    ? `${practitionerName}${practitionerSignedOn &&
                                        practitionerSignedOn !== "—"
                                        ? ` (signed on ${practitionerSignedOn})`
                                        : ""
                                    }`
                                    : "—"
                            }
                        />
                        <Row
                            label="Staff Signature"
                            value={
                                staffName
                                    ? `${staffName}${staffSignedOn && staffSignedOn !== "—"
                                        ? ` (signed on ${staffSignedOn})`
                                        : ""
                                    }`
                                    : "—"
                            }
                        />
                        <Row
                            label="Supervisor Signature"
                            value={
                                supervisorName
                                    ? `${supervisorName}${supervisorSignedOn && supervisorSignedOn !== "—"
                                        ? ` (signed on ${supervisorSignedOn})`
                                        : ""
                                    }`
                                    : "—"
                            }
                        />
                        <Row
                            label="Treatment Director Signature"
                            value={
                                directorName
                                    ? `${directorName}${directorSignedOn && directorSignedOn !== "—"
                                        ? ` (signed on ${directorSignedOn})`
                                        : ""
                                    }`
                                    : "—"
                            }
                        />
                    </tbody>
                </table>
            </Box>
        </Paper>
    );
};

export default MHISClientFormPreview;